import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Smartphone } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { verifyOtpSchema, type VerifyOtpInput } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { FloatingField } from '../../../components/FloatingField';
import { useLogout, useVerifyOtp } from '../api/auth-api';
import { AuthError, AuthLayout } from '../components/AuthLayout';
import { safeReturnPath } from '../return-url';
import { applyFieldErrors, hasFieldErrors } from './LoginPage';
import styles from './auth-forms.module.css';

/**
 * AUTH-06 — the code challenge, between a correct password and being signed in.
 *
 * One box takes either the app's six digits or a recovery code, because this is the
 * screen somebody reaches when their phone is lost and sending them somewhere else
 * to say so helps nobody. The server accepts both.
 */
export function OtpChallengePage(): ReactNode {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { next?: string; remember?: string };
  const verify = useVerifyOtp();
  const logout = useLogout();
  const [isUsingRecoveryCode, setIsUsingRecoveryCode] = useState(false);

  const form = useForm<VerifyOtpInput>({
    resolver: zodResolver(verifyOtpSchema),
    defaultValues: { code: '' },
    mode: 'onSubmit',
  });

  useEffect(() => {
    form.setFocus('code');
  }, [form, isUsingRecoveryCode]);

  const onSubmit = form.handleSubmit(async (values) => {
    const succeeded = await verify
      // "Keep me signed in" was answered on the sign-in form one screen ago; this
      // is where the cookie it describes is actually issued.
      .mutateAsync({ ...values, rememberMe: search.remember !== '0' })
      .then(() => true)
      .catch((error: unknown) => {
        applyFieldErrors(error, form.setError);
        return false;
      });
    if (!succeeded) return;
    await navigate({ to: safeReturnPath(search.next) });
  });

  return (
    <AuthLayout
      title="Enter your code"
      icon={<Smartphone size={20} aria-hidden />}
      subtitle={
        isUsingRecoveryCode
          ? 'Type one of the recovery codes you saved when you set this up.'
          : 'Open your authenticator app and type the 6-digit code it shows.'
      }
      footer={
        <button
          type="button"
          className={styles.link}
          onClick={() => logout.mutate()}
        >
          Sign in as somebody else
        </button>
      }
    >
      <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
        {verify.error && !hasFieldErrors(verify.error) && (
          <AuthError message={verify.error.message} />
        )}

        <div className={isUsingRecoveryCode ? undefined : styles.otp}>
          <FloatingField
            {...form.register('code')}
            label={isUsingRecoveryCode ? 'Recovery code' : 'Code'}
            error={form.formState.errors.code?.message}
            isRequired
            inputMode={isUsingRecoveryCode ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            maxLength={isUsingRecoveryCode ? 20 : 6}
            placeholder={isUsingRecoveryCode ? 'ABCDE-FGHJK' : '000000'}
          />
        </div>

        <div className={styles.actions}>
          <Button type="submit" variant="solid" size="lg" block isPending={verify.isPending}>
            Continue
          </Button>
          <button
            type="button"
            className={`${styles.link} ${styles.center}`}
            onClick={() => {
              setIsUsingRecoveryCode((current) => !current);
              form.reset({ code: '' });
            }}
          >
            {isUsingRecoveryCode ? 'Use my authenticator app instead' : "I don't have my phone"}
          </button>
        </div>
      </form>
    </AuthLayout>
  );
}
